import { Button as ButtonPrimitive } from "@base-ui/react/button";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const listCardStyles = cn(
  "group relative flex min-h-[140px] w-full flex-col items-stretch justify-center overflow-hidden",
  "min-w-0 flex-1",
  "border-0 border-b border-solid border-b-outline-subtle",
  "bg-surface",
  "p-(--spacing-16)",
  "text-left",
  "transition-colors duration-200 ease-in-out",
  "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-(--spacing-4)",
  "after:bg-primary after:opacity-0 after:transition-opacity after:duration-200 after:ease-in-out after:content-['']",
  "data-[state=active]:bg-primary/8 data-[state=active]:after:opacity-100",
  "cursor-pointer",
  "hover:bg-surface-hover",
  "active:bg-surface-pressed",
  "focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring",
  "disabled:pointer-events-none disabled:opacity-50",
);

export interface ListCardProps
  extends Omit<ButtonPrimitive.Props, "className" | "children"> {
  /** Whether this list card is the selected item. */
  active?: boolean;
  /** Flexible card contents composed by the consumer. */
  children: ReactNode;
}

/**
 * Card-like list item surface. Compose title, text, chips, and other content
 * inside it. Parent lists own selected state and click behavior.
 */
function ListCard({ active = false, children, ...props }: ListCardProps) {
  const visualState = active ? "active" : "default";

  return (
    <ButtonPrimitive
      type="button"
      data-slot="list-card"
      data-state={visualState}
      aria-pressed={active}
      className={listCardStyles}
      {...props}
    >
      {children}
    </ButtonPrimitive>
  );
}

export { ListCard };
